const recommendationsCache = new Map();
const CART_RECOMMENDATIONS_ID = 'cart-drawer-recommendations';
const CART_RECOMMENDATIONS_READY_TIMEOUT = 700;

function waitForAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function waitForTimeout(delay) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

function waitForImage(image) {
  if (!(image instanceof HTMLImageElement) || image.complete) {
    return Promise.resolve();
  }

  if (image.decode) {
    return image.decode().catch(() => {});
  }

  return new Promise((resolve) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', resolve, { once: true });
  });
}

function escapeSelectorValue(value) {
  if (window.CSS?.escape) {
    return window.CSS.escape(value);
  }

  return value.replace(/["\\]/g, '\\$&');
}

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observes relevant attribute changes for recommendation reloads.
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(() => {
    this.#loadRecommendations();
  });

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The last fully loaded request key
   * @type {string | null}
   */
  #loadedRequestKey = null;

  /**
   * Incremented to ensure stale async loads do not win races.
   * @type {number}
   */
  #loadVersion = 0;

  connectedCallback() {
    if (this.dataset.recommendationsPerformed === 'true') {
      this.#loadedRequestKey = this.#getRequestKey();
    }

    if (this.#shouldLoadImmediately()) {
      this.loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }

    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: [
        'data-url',
        'data-product-id',
        'data-product-ids',
        'data-section-id',
        'data-intent',
        'data-cache',
      ],
    });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const requestUrls = this.#getRecommendationUrls();
    const requestKey = requestUrls.join('|');

    if (!requestKey) {
      return;
    }

    if (this.#loadedRequestKey === requestKey && this.#hasRenderedRecommendations()) {
      return;
    }

    const loadVersion = ++this.#loadVersion;
    this.#activeFetch?.abort();

    try {
      for (const requestUrl of requestUrls) {
        const result = await this.#fetchCachedRecommendations(requestUrl);

        if (loadVersion !== this.#loadVersion) {
          return;
        }

        if (!result.success) {
          continue;
        }

        const recommendations = this.#extractRecommendations(result.data);

        if (!recommendations?.innerHTML || !recommendations.innerHTML.trim().length) {
          continue;
        }

        this.#cacheRecommendations(requestUrl, result.data);
        const didRender = await this.#renderRecommendations(recommendations.innerHTML, loadVersion);

        if (!didRender || loadVersion !== this.#loadVersion) {
          return;
        }

        this.#setRecommendationsPerformed('true');
        delete this.dataset.error;
        this.classList.remove('hidden');
        this.#loadedRequestKey = requestKey;
        return;
      }

      this.#handleError(new Error('No recommendations available'));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      this.#handleError(error);
    }
  }

  loadRecommendations() {
    return this.#loadRecommendations();
  }

  /**
   * Returns the request key for the current attributes.
   * @returns {string | null}
   */
  #getRequestKey() {
    const requestUrls = this.#getRecommendationUrls();

    if (requestUrls.length === 0) {
      return null;
    }

    return requestUrls.join('|');
  }

  /**
   * Cart drawer recommendations should begin loading as soon as the drawer is open.
   * @returns {boolean}
   */
  #shouldLoadImmediately() {
    return this.#isCartDrawerRecommendations() && this.closest('dialog[open]') != null;
  }

  /**
   * Builds recommendation request URLs in priority order.
   * @returns {string[]}
   */
  #getRecommendationUrls() {
    const urlValue = this.dataset.url;

    if (!urlValue) {
      throw new Error('A data-url attribute is required');
    }

    const baseUrl = new URL(urlValue, window.location.origin);
    const { sectionId, intent } = this.dataset;

    if (sectionId && !baseUrl.searchParams.get('section_id')) {
      baseUrl.searchParams.set('section_id', sectionId);
    }

    if (intent && !baseUrl.searchParams.get('intent')) {
      baseUrl.searchParams.set('intent', intent);
    }

    const productIds = this.#getProductIds(baseUrl.searchParams.get('product_id'));

    return productIds.map((productId) => {
      const url = new URL(baseUrl);
      url.searchParams.set('product_id', productId);
      return url.toString();
    });
  }

  /**
   * Returns the prioritized list of product IDs to try.
   * @param {string | null} fallbackProductId
   * @returns {string[]}
   */
  #getProductIds(fallbackProductId) {
    const candidates = [];

    if (this.dataset.productIds) {
      candidates.push(...this.dataset.productIds.split(','));
    }

    if (this.dataset.productId) {
      candidates.push(this.dataset.productId);
    }

    if (fallbackProductId) {
      candidates.push(fallbackProductId);
    }

    const uniqueProductIds = new Set();

    return candidates
      .map((productId) => productId.trim())
      .filter((productId) => {
        if (!productId || uniqueProductIds.has(productId)) {
          return false;
        }

        uniqueProductIds.add(productId);
        return true;
      });
  }

  /**
   * Extracts the product recommendations element from the fetched HTML.
   * @param {string} html
   * @returns {Element | null}
   */
  #extractRecommendations(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(`product-recommendations[id="${escapeSelectorValue(this.id)}"]`);
  }

  /**
   * Renders recommendation content, staging cart drawer carousels until layout is ready.
   * @param {string} html
   * @param {number} loadVersion
   * @returns {Promise<boolean>}
   */
  async #renderRecommendations(html, loadVersion) {
    if (!this.#isCartDrawerRecommendations()) {
      this.innerHTML = html;
      return true;
    }

    const stage = document.createElement('div');
    stage.className = 'cart-recommendations__stage';
    stage.setAttribute('aria-hidden', 'true');
    stage.innerHTML = html;
    this.append(stage);

    try {
      await this.#prepareCartRecommendationsStage(stage);

      if (loadVersion !== this.#loadVersion) {
        stage.remove();
        return false;
      }

      this.replaceChildren(...Array.from(stage.childNodes));
      return true;
    } catch (error) {
      stage.remove();
      throw error;
    }
  }

  /**
   * Lets the staged cart carousel initialize before replacing the skeleton.
   * @param {HTMLElement} stage
   */
  async #prepareCartRecommendationsStage(stage) {
    await Promise.race([customElements.whenDefined('slideshow-component'), waitForTimeout(CART_RECOMMENDATIONS_READY_TIMEOUT)]);

    this.#resetCartRecommendationsScroll(stage);
    await waitForAnimationFrame();
    this.#resetCartRecommendationsScroll(stage);
    await Promise.race([this.#waitForVisibleCartRecommendationImages(stage), waitForTimeout(CART_RECOMMENDATIONS_READY_TIMEOUT)]);
  }

  /**
   * Resets the staged cart carousel to its first rendered slide.
   * @param {HTMLElement} stage
   */
  #resetCartRecommendationsScroll(stage) {
    const scroller = stage.querySelector('slideshow-slides');
    const firstSlide = stage.querySelector('slideshow-slide');

    if (!(scroller instanceof HTMLElement) || !(firstSlide instanceof HTMLElement)) return;

    const paddingInlineStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
    scroller.scrollTo({
      left: firstSlide.offsetLeft - paddingInlineStart,
      behavior: 'instant',
    });
  }

  /**
   * Waits briefly for images that are visible or peeking in the cart carousel.
   * @param {HTMLElement} stage
   */
  async #waitForVisibleCartRecommendationImages(stage) {
    const images = Array.from(
      stage.querySelectorAll(
        "slideshow-slide[aria-hidden='false'] img, slideshow-slide[aria-hidden='false'] + slideshow-slide img"
      )
    );

    await Promise.all(images.map(waitForImage));
  }

  /**
   * Whether this element already contains fetched recommendation content.
   * @returns {boolean}
   */
  #hasRenderedRecommendations() {
    return this.innerHTML.trim().length > 0 && this.querySelector('.cart-recommendations__skeleton') == null;
  }

  /**
   * Whether this element is the cart drawer recommendations component.
   * @returns {boolean}
   */
  #isCartDrawerRecommendations() {
    return this.id === CART_RECOMMENDATIONS_ID;
  }

  /**
   * Whether recommendation responses should use the shared memory cache.
   * @returns {boolean}
   */
  #shouldUseCache() {
    return this.dataset.cache !== 'false';
  }

  /**
   * Updates the performed state without creating redundant mutations.
   * @param {'true' | 'false'} value
   */
  #setRecommendationsPerformed(value) {
    if (this.dataset.recommendationsPerformed === value) return;

    this.dataset.recommendationsPerformed = value;
  }

  /**
   * Stores a valid recommendation response for future use.
   * @param {string} url
   * @param {string} html
   */
  #cacheRecommendations(url, html) {
    if (!this.#shouldUseCache()) return;

    recommendationsCache.set(url, html);
  }

  /**
   * Fetches recommendations, using the shared cache when enabled.
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(url) {
    const useCache = this.#shouldUseCache();
    const cachedResponse = useCache ? recommendationsCache.get(url) : null;

    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, {
        cache: useCache ? 'default' : 'no-store',
        signal: this.#activeFetch.signal,
      });

      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.#setRecommendationsPerformed('false');
    this.dataset.error = 'Error loading product recommendations';
    this.#loadedRequestKey = null;
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
